import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  IconButton,
  InputAdornment,
  Divider,
} from '@mui/material';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { Visibility, VisibilityOff, ArrowForward, AccountBalance } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { stitchColors } from '../theme/tokens';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

const AUTH_HERO_IMAGE =
  "url('https://lh3.googleusercontent.com/aida-public/AB6AXuCwBeeE8zEq6KOf48iMoggxu6lV3XIePgydLswAiFLYiK5kvKmvaw3CM06RXMIBeGdlO1oUJIGZeqlXhgCqcTM0Szcfne8Tt9cWep-YbDFG1be0NtqT6YqFS-2az6xVfbqHgwNBF4S-lIdJ18A2ZQzRtjc3YJ_wLu-mKbC0oK4WH3nQmHXzE4xrwZzqJr-aTnqQ6KHzFBJjmWaKILZ4zzPU_-H4nk20fjYC8Mv42KCoy1QS-ksiQSSGXoGeTsIWvfyN-Ebiuyh5Refy')";

const Login: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWithGoogle } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const safeRedirectTo =
    redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//') ? redirectTo : '/';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      setLoading(true);
      setError('');
      await login(data.email, data.password);
      navigate(safeRedirectTo, { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        bgcolor: stitchColors.background,
        color: stitchColors.onBackground,
      }}
    >
      <Box
        component="main"
        sx={{
          width: { xs: '100%', lg: '50%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2, sm: 4, lg: 8 },
          py: { xs: 4, lg: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <header>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: stitchColors.primary, mb: 2 }}>
              <AccountBalance sx={{ fontSize: 32 }} />
              <Typography
                sx={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Australian Wills
              </Typography>
            </Box>
            <Typography variant="h1" component="h1" sx={{ mb: 1 }}>
              Secure Access
            </Typography>
            <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, color: stitchColors.onSurfaceVariant }}>
              Log in to manage your estate documents and monitor your vault.
            </Typography>
          </header>

          {error && (
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box>
                <Typography
                  component="label"
                  htmlFor="login-email"
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    color: stitchColors.onSurface,
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  id="login-email"
                  fullWidth
                  autoComplete="email"
                  placeholder="client@example.com"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                  <Typography
                    component="label"
                    htmlFor="login-password"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                      color: stitchColors.onSurface,
                    }}
                  >
                    Password
                  </Typography>
                  <Link href="#" variant="body2" sx={{ color: stitchColors.onSurfaceVariant, fontWeight: 600 }}>
                    Forgot Password?
                  </Link>
                </Box>
                <TextField
                  id="login-password"
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••••••"
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              endIcon={<ArrowForward />}
              sx={{
                mt: 4,
                py: 1.75,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {loading ? 'Signing in…' : 'Access Vault'}
            </Button>

            <Divider sx={{ my: 3 }}>OR</Divider>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={async (credentialResponse: CredentialResponse) => {
                  try {
                    setLoading(true);
                    setError('');
                    if (credentialResponse.credential) {
                      await loginWithGoogle(credentialResponse.credential);
                      navigate(safeRedirectTo, { replace: true });
                    }
                  } catch (err: any) {
                    setError(err.message);
                    setLoading(false);
                  }
                }}
                onError={() => {
                  setError('Google Login Failed');
                  setLoading(false);
                }}
                useOneTap
              />
            </Box>

            <Typography
              sx={{
                mt: 3,
                textAlign: 'center',
                fontSize: '0.875rem',
                color: stitchColors.onSurfaceVariant,
                lineHeight: 1.6,
              }}
            >
              Don&apos;t have an account?{' '}
              <Link
                component={RouterLink}
                to="/register"
                fontWeight={600}
                sx={{ color: stitchColors.primary }}
              >
                Register
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        component="aside"
        aria-hidden
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: { lg: '50%' },
          position: 'relative',
          minHeight: { lg: '100vh' },
          bgcolor: stitchColors.surfaceVariant,
          borderLeft: `1px solid ${stitchColors.outlineVariant}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: AUTH_HERO_IMAGE,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(to top right, ${stitchColors.surfaceVariant}66, transparent)`,
          }}
        />
      </Box>
    </Box>
  );
};

export default Login;
